import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, ExternalLink, ShieldCheck, RefreshCw, Gauge } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, Card, Spinner, StatusBadge } from "@/components/common";

export default function Websites() {
  const { t } = useI18n();
  const [clients, setClients] = useState(null);
  const [checks, setChecks] = useState({});
  const [busy, setBusy] = useState(false);
  const [single, setSingle] = useState(null);

  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => { load(); }, []);
  if (!clients) return <Spinner />;

  const checkAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/websites/check-all");
      const map = {}; data.forEach((d) => { map[d.client_id] = d; });
      setChecks(map);
      await load();
      toast.success(t("Alle Websites geprüft"));
    } catch { toast.error(t("Prüfung fehlgeschlagen")); } finally { setBusy(false); }
  };

  const checkOne = async (cid) => {
    setSingle(cid);
    try {
      const { data } = await api.post(`/websites/${cid}/check`);
      setChecks((c) => ({ ...c, [cid]: data }));
      await load();
      toast.success(t("Geprüft"));
    } catch { toast.error(t("Prüfung fehlgeschlagen")); } finally { setSingle(null); }
  };

  return (
    <div className="fade-in">
      <PageHeader title={t("websites")} subtitle={t("Status aller betreuten Websites · Live-Prüfung")}>
        <button onClick={checkAll} disabled={busy} data-testid="check-all-btn"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg transition-colors disabled:opacity-60 flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {t("Alle prüfen")}
        </button>
      </PageHeader>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
        {clients.map((c) => {
          const chk = checks[c.id];
          return (
            <Card key={c.id} className="p-5 hover:border-white/20 transition-colors">
              <div className="flex items-center justify-between mb-3">
                <Globe className="w-5 h-5 text-gray-500" />
                <StatusBadge status={chk?.status || c.website_status} />
              </div>
              <Link to={`/admin/clients/${c.id}`} className="font-bold hover:text-[#A3E635] transition-colors">{c.company_name}</Link>
              <a href={`https://${c.domain}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sm text-gray-500 hover:text-white mt-1">
                {c.domain} <ExternalLink className="w-3 h-3" />
              </a>
              <div className="flex items-center gap-3 mt-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><ShieldCheck className={`w-4 h-4 ${(chk?.ssl ?? true) ? "text-[#A3E635]" : "text-red-400"}`} /> SSL</span>
                {chk?.response_ms != null && <span className="flex items-center gap-1"><Gauge className="w-4 h-4" /> {chk.response_ms} ms</span>}
                <span>{c.package?.name}</span>
              </div>
              <button onClick={() => checkOne(c.id)} disabled={single === c.id} data-testid={`check-${c.id}`}
                className="mt-4 w-full bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-2 text-sm flex items-center justify-center gap-2 transition-colors">
                <RefreshCw className={`w-4 h-4 ${single === c.id ? "animate-spin" : ""}`} /> {t("Jetzt prüfen")}
              </button>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
